import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { LogOut, Download, X, Plus, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
import founderPortrait from '@/assets/founder-portrait.jpg';
import gracePortrait from '@/assets/partner-grace-gakii.jpg';

interface QImage {
  dataUrl: string;
  width: number;
  height: number;
  format: 'PNG' | 'JPEG';
}

interface Author {
  name: string;
  role: string;
  photo: QImage | null;
}

const readImage = (file: File): Promise<QImage> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Not an image'));
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        const format: 'PNG' | 'JPEG' = file.type === 'image/png' ? 'PNG' : 'JPEG';
        resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight, format });
      };
      img.onerror = () => reject(new Error('Could not load image'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

const urlToQImage = (url: string): Promise<QImage> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas'));
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.92), width: img.naturalWidth, height: img.naturalHeight, format: 'JPEG' });
    };
    img.onerror = () => reject(new Error('img load'));
    img.src = url;
  });

// ---------------- Brand accent (golden) ----------------
const GOLD_HEX = '#D49A3D';
const GOLD_RGB: [number, number, number] = [212, 154, 61];

// ---------------- Body parser (shared by preview + PDF generator) ----------------
type PreviewBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'subheading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'quote'; text: string };

const parseBodyBlocks = (raw: string): PreviewBlock[] => {
  const out: PreviewBlock[] = [];
  const chunks = raw.split(/\n{2,}/).map((c) => c.trim()).filter(Boolean);
  for (const chunk of chunks) {
    const lines = chunk.split('\n').map((l) => l.trim());
    if (lines.every((l) => l.startsWith('- ') || l.startsWith('* '))) {
      out.push({ kind: 'list', items: lines.map((l) => l.replace(/^[-*]\s+/, '')) });
    } else if (lines[0].startsWith('## ')) {
      out.push({ kind: 'subheading', text: lines[0].slice(3).trim() });
      const rest = lines.slice(1).join(' ').trim();
      if (rest) out.push({ kind: 'paragraph', text: rest });
    } else if (lines[0].startsWith('# ')) {
      out.push({ kind: 'heading', text: lines[0].slice(2).trim() });
      const rest = lines.slice(1).join(' ').trim();
      if (rest) out.push({ kind: 'paragraph', text: rest });
    } else if (lines[0].startsWith('> ')) {
      out.push({ kind: 'quote', text: lines.join(' ').replace(/^>\s+/, '') });
    } else {
      out.push({ kind: 'paragraph', text: lines.join(' ') });
    }
  }
  return out;
};

// Render inline **bold** segments
const renderInline = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? (
      <strong key={i} className="font-bold">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

// ---------------- Live preview (scaled A4 approximation of the PDF) ----------------
interface PreviewProps {
  title: string;
  subtitle: string;
  issueNumber: string;
  monthYear: string;
  startPage: string;
  heroImage: QImage | null;
  pullQuote: string;
  authors: Author[];
  blocks: PreviewBlock[];
}

const renderBlock = (b: PreviewBlock, i: number) => {
  if (b.kind === 'heading') {
    return <p key={i} className="font-serif font-bold mt-2 mb-1 text-[10px] leading-tight" style={{ color: GOLD_HEX }}>{b.text}</p>;
  }
  if (b.kind === 'subheading') {
    return <p key={i} className="font-serif font-bold mt-1.5 mb-1 text-[8.5px] leading-tight" style={{ color: GOLD_HEX }}>{b.text}</p>;
  }
  if (b.kind === 'list') {
    return (
      <ul key={i} className="font-serif text-[7px] leading-snug text-gray-800 mb-1 pl-2">
        {b.items.map((it, j) => (
          <li key={j} className="mb-0.5">• {renderInline(it)}</li>
        ))}
      </ul>
    );
  }
  if (b.kind === 'quote') {
    return <p key={i} className="font-serif italic text-[7px] leading-snug mb-1" style={{ color: GOLD_HEX }}>"{renderInline(b.text)}"</p>;
  }
  return <p key={i} className="font-serif text-[7px] leading-snug text-gray-800 mb-1 text-justify">{renderInline(b.text)}</p>;
};

// Rough height estimator in preview pixels (col width ~160px, body font 7px)
const CHARS_PER_LINE = 32;
const BODY_LINE_H = 9.5;
const estimateBlockH = (b: PreviewBlock): number => {
  if (b.kind === 'heading') return 14 + 10;
  if (b.kind === 'subheading') return 12 + 8;
  if (b.kind === 'quote') {
    const lines = Math.max(1, Math.ceil(b.text.length / CHARS_PER_LINE));
    return lines * BODY_LINE_H + 6;
  }
  if (b.kind === 'list') {
    return b.items.reduce((acc, it) => acc + Math.max(1, Math.ceil(it.length / (CHARS_PER_LINE - 2))) * BODY_LINE_H, 0) + 4;
  }
  const lines = Math.max(1, Math.ceil(b.text.length / CHARS_PER_LINE));
  return lines * BODY_LINE_H + 6;
};

// Flow blocks sequentially into 4 column slots (P1L, P1R, P2L, P2R).
const flowBlocks = (blocks: PreviewBlock[], capacities: [number, number, number, number]): PreviewBlock[][] => {
  const cols: PreviewBlock[][] = [[], [], [], []];
  const used = [0, 0, 0, 0];
  let idx = 0;
  for (const b of blocks) {
    const h = estimateBlockH(b);
    while (idx < 4 && used[idx] + h > capacities[idx]) idx++;
    if (idx >= 4) break;
    cols[idx].push(b);
    used[idx] += h;
  }
  return cols;
};

const QuickOnePreview: React.FC<PreviewProps> = ({
  title,
  subtitle,
  issueNumber,
  monthYear,
  startPage,
  heroImage,
  pullQuote,
  authors,
  blocks,
}) => {
  // A4 page rendered at ~360px wide for the preview (height ≈ 509)
  const pageStyle: React.CSSProperties = {
    width: 360,
    aspectRatio: '1 / 1.414',
    background: 'white',
    boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    position: 'relative',
  };

  const startP = parseInt(startPage, 10) || 12;

  // Page-1 column capacity is small (hero + title chrome eats space); page-2 columns are tall.
  // P2R also reserves space for the pull-quote at the top.
  const quoteReserve = pullQuote.trim() ? 50 : 0;
  const PAGE1_COL_H = 180;
  const PAGE2_COL_H = 460;
  const [p1l, p1r, p2l, p2r] = flowBlocks(blocks, [
    PAGE1_COL_H,
    PAGE1_COL_H,
    PAGE2_COL_H,
    PAGE2_COL_H - quoteReserve,
  ]);

  return (
    <div className="space-y-4 flex flex-col items-center">
      {/* PAGE 1 */}
      <div style={pageStyle}>
        {/* Hero */}
        <div className="w-full h-[180px] bg-gray-200 overflow-hidden">
          {heroImage ? (
            <img src={heroImage.dataUrl} alt="Hero" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400">Hero image</div>
          )}
        </div>

        <div className="px-4 pt-3">
          {/* Authors */}
          <div className="flex flex-wrap gap-3 mb-2">
            {authors.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {a.photo ? (
                  <img src={a.photo.dataUrl} alt={a.name} className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gray-200" />
                )}
                <div className="leading-tight">
                  <div className="font-serif font-bold text-[7px]" style={{ color: GOLD_HEX }}>{a.name || '—'}</div>
                  <div className="font-serif text-[6px] text-gray-500">{a.role}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Title */}
          <h1 className="font-serif uppercase leading-tight" style={{ fontSize: 14, color: GOLD_HEX }}>
            {title || 'Article title appears here'}
          </h1>
          {subtitle && (
            <p className="font-serif uppercase text-[7px] leading-snug mt-1" style={{ color: '#000080' }}>{subtitle}</p>
          )}
          <div className="w-12 h-[2px] mt-1.5 mb-2" style={{ background: GOLD_HEX }} />

          {/* Body (2 cols) */}
          <div className="grid grid-cols-2 gap-2">
            <div>{p1l.map((b, i) => renderBlock(b, i))}</div>
            <div>{p1r.map((b, i) => renderBlock(b, i))}</div>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-2 left-4 right-4 font-serif italic text-[6px] text-gray-500">
          {startP} | Issue No. {issueNumber} | {monthYear}
        </div>
      </div>

      {/* PAGE 2 */}
      <div style={pageStyle}>
        <div className="px-4 pt-4 grid grid-cols-2 gap-2">
          <div>{p2l.map((b, i) => renderBlock(b, i))}</div>
          <div>
            {pullQuote.trim() && (
              <div className="border-t border-b py-1.5 my-0.5 mb-2" style={{ borderColor: GOLD_HEX }}>
                <p className="font-serif italic font-bold text-[8px] leading-snug" style={{ color: GOLD_HEX }}>
                  {pullQuote}
                </p>
              </div>
            )}
            {p2r.map((b, i) => renderBlock(b, i))}
          </div>
        </div>

        <div className="absolute bottom-2 left-4 right-4 font-serif italic text-[6px] text-gray-500 text-right">
          Issue No. {issueNumber} | {monthYear} | {startP + 1}
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Live preview · Approximate layout. The downloaded PDF is the source of truth.
      </p>
    </div>
  );
};

const AdminQuickOne = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const today = new Date();
  const defaultMonthYear = today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [issueNumber, setIssueNumber] = useState('1');
  const [monthYear, setMonthYear] = useState(defaultMonthYear);
  const [startPage, setStartPage] = useState('12');
  const [heroImage, setHeroImage] = useState<QImage | null>(null);
  const [pullQuote, setPullQuote] = useState('');
  const [sections, setSections] = useState<{ heading: string; content: string }[]>([
    { heading: '', content: '' },
  ]);

  // Compose the body text the parser/PDF use from the structured sections.
  const bodyText = sections
    .map((s) => {
      const h = s.heading.trim();
      const c = s.content.trim();
      if (h && c) return `# ${h}\n\n${c}`;
      if (h) return `# ${h}`;
      return c;
    })
    .filter(Boolean)
    .join('\n\n');

  const updateSection = (idx: number, patch: Partial<{ heading: string; content: string }>) =>
    setSections((arr) => arr.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const addSection = () => setSections((arr) => [...arr, { heading: '', content: '' }]);
  const removeSection = (idx: number) => setSections((arr) => arr.filter((_, i) => i !== idx));
  const [authors, setAuthors] = useState<Author[]>([
    { name: 'Omollo Victor', role: 'Trainee Advocate | omollovictor81@gmail.com', photo: null },
  ]);

  useEffect(() => {
    const check = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/admin/login', { replace: true });
        return;
      }
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', sessionData.session.user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (!roleData) {
        toast({ title: 'Access denied', description: 'Your account is not an admin.', variant: 'destructive' });
        await supabase.auth.signOut();
        navigate('/admin/login', { replace: true });
        return;
      }
      setIsAdmin(true);
      setAuthChecking(false);
    };
    check();
  }, [navigate, toast]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  const onImage = async (e: React.ChangeEvent<HTMLInputElement>, setter: (i: QImage) => void) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Max 8MB', variant: 'destructive' });
      return;
    }
    try {
      setter(await readImage(f));
    } catch (err: any) {
      toast({ title: 'Image error', description: err.message, variant: 'destructive' });
    }
    e.target.value = '';
  };

  const updateAuthor = (idx: number, patch: Partial<Author>) => {
    setAuthors((arr) => arr.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const addAuthor = () => {
    if (authors.length >= 3) return;
    setAuthors((a) => [...a, { name: '', role: '', photo: null }]);
  };

  const removeAuthor = (idx: number) => {
    setAuthors((a) => a.filter((_, i) => i !== idx));
  };

  const handleDownload = async () => {
    if (!title.trim()) {
      toast({ title: 'Missing title', description: 'Add a title', variant: 'destructive' });
      return;
    }
    if (!bodyText.trim()) {
      toast({ title: 'Missing content', description: 'Add body text', variant: 'destructive' });
      return;
    }
    if (!heroImage) {
      toast({ title: 'Missing hero image', description: 'Upload a hero image for the top of page 1', variant: 'destructive' });
      return;
    }

    const ORANGE: [number, number, number] = GOLD_RGB; // brand golden accent
    const TEXT: [number, number, number] = [30, 30, 40];
    const MUTED: [number, number, number] = [110, 110, 120];
    const WHITE: [number, number, number] = [255, 255, 255];

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const marginX = 42;
    const contentW = pageW - marginX * 2;
    const colGap = 24;
    const colW = (contentW - colGap) / 2;
    const colXLeft = marginX;
    const colXRight = marginX + colW + colGap;

    const blocks = parseBodyBlocks(bodyText);

    // ---------- PAGE 1 ----------
    // Hero image full-width at top (cover-fit)
    const heroH = 380;
    {
      const ratio = heroImage.width / heroImage.height;
      const targetRatio = pageW / heroH;
      let dw = pageW, dh = heroH, dx = 0, dy = 0;
      if (ratio > targetRatio) {
        dh = heroH;
        dw = dh * ratio;
        dx = (pageW - dw) / 2;
      } else {
        dw = pageW;
        dh = dw / ratio;
        dy = (heroH - dh) / 2;
      }
      doc.addImage(heroImage.dataUrl, heroImage.format, dx, dy, dw, dh, undefined, 'FAST');
    }

    // Author byline row (just below hero)
    let bylineY = heroH + 30;
    {
      const photoSize = 32;
      const slotW = contentW / Math.max(authors.length, 1);
      authors.forEach((a, i) => {
        const slotX = marginX + i * slotW;
        let textX = slotX;
        if (a.photo) {
          // circular-ish photo using oval clip (jsPDF lacks clip — approximate with rounded rect mask)
          doc.addImage(a.photo.dataUrl, a.photo.format, slotX, bylineY - photoSize / 2 - 4, photoSize, photoSize, undefined, 'FAST');
          textX = slotX + photoSize + 10;
        }
        doc.setFont('times', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(...ORANGE);
        doc.text(a.name || '', textX, bylineY - 2);
        doc.setFont('times', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...MUTED);
        doc.text(a.role || '', textX, bylineY + 11);
      });
    }

    // Big serif title (gold)
    let titleY = bylineY + 40;
    doc.setFont('times', 'normal');
    doc.setFontSize(22);
    doc.setTextColor(...ORANGE);
    const titleLines = doc.splitTextToSize(title.toUpperCase(), contentW) as string[];
    titleLines.forEach((ln) => {
      doc.text(ln, marginX, titleY);
      titleY += 24;
    });

    // Subtitle (uppercase, gold)
    if (subtitle.trim()) {
      doc.setFont('times', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 128);
      const subLines = doc.splitTextToSize(subtitle.toUpperCase(), contentW) as string[];
      subLines.forEach((ln) => {
        doc.text(ln, marginX, titleY);
        titleY += 14;
      });
    }

    // Short orange rule under title
    titleY += 6;
    doc.setDrawColor(...ORANGE);
    doc.setLineWidth(2.2);
    doc.line(marginX, titleY, marginX + 70, titleY);
    titleY += 18;

    // ---------- BODY: 2-column flow across page 1 + page 2 ----------
    let page = 1; // 1 or 2
    let col: 0 | 1 = 0;
    let cy = titleY;
    const pageBottom = pageH - 50;
    const page2Top = 50;
    let quotePlaced = false;
    const quoteText = pullQuote.trim();

    const colX = () => (col === 0 ? colXLeft : colXRight);
    const colTop = () => (page === 1 ? titleY : page2Top);

    // Reserve a top block on page 2 right column for the pull quote
    const quoteH = quoteText ? estimateQuoteHeight() : 0;
    function estimateQuoteHeight(): number {
      doc.setFont('times', 'bolditalic');
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(quoteText, colW - 16) as string[];
      return 14 + lines.length * 15 + 14; // rules + text
    }

    const nextColumnOrPage = () => {
      if (page === 1 && col === 0) {
        col = 1;
        cy = colTop();
      } else if (page === 1 && col === 1) {
        // move to page 2 left col
        drawFooter(1);
        doc.addPage();
        page = 2;
        col = 0;
        cy = page2Top;
      } else if (page === 2 && col === 0) {
        col = 1;
        cy = page2Top;
        // pull-quote sits at top of page 2 right column
        if (quoteText && !quotePlaced) {
          drawPullQuote();
          quotePlaced = true;
        }
      } else {
        // Out of room on a 2-page layout. Stop adding content silently.
        // (Quick One is strictly two pages.)
        cy = pageBottom + 1000;
      }
    };

    const drawPullQuote = () => {
      const x = colX();
      const w = colW;
      doc.setDrawColor(...ORANGE);
      doc.setLineWidth(1);
      doc.line(x, cy, x + w, cy);
      cy += 14;
      doc.setFont('times', 'bolditalic');
      doc.setFontSize(11.5);
      doc.setTextColor(...ORANGE);
      const lines = doc.splitTextToSize(quoteText, w) as string[];
      lines.forEach((ln) => {
        doc.text(ln, x, cy);
        cy += 15;
      });
      cy += 4;
      doc.line(x, cy, x + w, cy);
      cy += 16;
    };

    const writeHeading = (text: string) => {
      const size = 11.5;
      const lineH = 15;
      doc.setFont('times', 'bold');
      doc.setFontSize(size);
      doc.setTextColor(...ORANGE);
      const lines = doc.splitTextToSize(text, colW) as string[];
      if (cy + lines.length * lineH > pageBottom) nextColumnOrPage();
      lines.forEach((ln) => {
        doc.text(ln, colX(), cy);
        cy += lineH;
      });
      cy += 4;
    };

    const writeSubheading = (text: string) => {
      const size = 10.5;
      const lineH = 13;
      doc.setFont('times', 'bold');
      doc.setFontSize(size);
      doc.setTextColor(...ORANGE);
      const lines = doc.splitTextToSize(text, colW) as string[];
      if (cy + lines.length * lineH > pageBottom) nextColumnOrPage();
      lines.forEach((ln) => {
        doc.text(ln, colX(), cy);
        cy += lineH;
      });
      cy += 3;
    };

    // Tokenize text into {text, bold} segments based on **bold** markers.
    const tokenize = (text: string): { text: string; bold: boolean }[] => {
      const out: { text: string; bold: boolean }[] = [];
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      for (const p of parts) {
        if (!p) continue;
        if (p.startsWith('**') && p.endsWith('**')) out.push({ text: p.slice(2, -2), bold: true });
        else out.push({ text: p, bold: false });
      }
      return out;
    };

    const writeParagraph = (text: string, opts: { italic?: boolean; size?: number; color?: [number, number, number] } = {}) => {
      const { italic = false, size = 10.5, color = TEXT } = opts;
      doc.setFontSize(size);
      const lineH = size * 1.4;
      const segments = tokenize(text);

      // Build words with bold flag; preserve spaces between words.
      type Word = { text: string; bold: boolean };
      const words: Word[] = [];
      segments.forEach((seg) => {
        const parts = seg.text.split(/(\s+)/);
        parts.forEach((p) => {
          if (p) words.push({ text: p, bold: seg.bold });
        });
      });

      const styleFor = (bold: boolean) =>
        bold ? (italic ? 'bolditalic' : 'bold') : italic ? 'italic' : 'normal';

      // Greedy wrap by measured width.
      let lineWords: Word[] = [];
      let lineWidth = 0;
      const flushLine = () => {
        if (!lineWords.length) return;
        if (cy + lineH > pageBottom) {
          nextColumnOrPage();
          if (cy > pageBottom) {
            lineWords = [];
            lineWidth = 0;
            return;
          }
        }
        let x = colX();
        doc.setTextColor(...color);
        for (const w of lineWords) {
          doc.setFont('times', styleFor(w.bold));
          doc.text(w.text, x, cy);
          x += doc.getTextWidth(w.text);
        }
        cy += lineH;
        lineWords = [];
        lineWidth = 0;
      };

      for (const w of words) {
        doc.setFont('times', styleFor(w.bold));
        const wWidth = doc.getTextWidth(w.text);
        if (lineWidth + wWidth > colW && lineWords.length) {
          // Strip trailing whitespace
          while (lineWords.length && /^\s+$/.test(lineWords[lineWords.length - 1].text)) {
            const removed = lineWords.pop()!;
            doc.setFont('times', styleFor(removed.bold));
            lineWidth -= doc.getTextWidth(removed.text);
          }
          flushLine();
          if (/^\s+$/.test(w.text)) continue;
        }
        lineWords.push(w);
        lineWidth += wWidth;
      }
      flushLine();
      cy += 6;
    };

    const writeList = (items: string[]) => {
      doc.setFont('times', 'normal');
      doc.setFontSize(10.5);
      doc.setTextColor(...TEXT);
      const lineH = 10.5 * 1.4;
      for (const item of items) {
        const lines = doc.splitTextToSize(item, colW - 14) as string[];
        for (let i = 0; i < lines.length; i++) {
          if (cy + lineH > pageBottom) {
            nextColumnOrPage();
            if (cy > pageBottom) return;
          }
          const prefix = i === 0 ? '•  ' : '   ';
          doc.text(prefix + lines[i], colX(), cy);
          cy += lineH;
        }
        cy += 2;
      }
      cy += 4;
    };

    const drawFooter = (pageNum: number) => {
      const num = (parseInt(startPage, 10) || 12) + (pageNum - 1);
      doc.setFont('times', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...MUTED);
      const label =
        pageNum === 1
          ? `${num} | Issue No. ${issueNumber} | ${monthYear}`
          : `Issue No. ${issueNumber} | ${monthYear} | ${num}`;
      const align = pageNum === 1 ? 'left' : 'right';
      const x = pageNum === 1 ? marginX : pageW - marginX;
      doc.text(label, x, pageH - 30, { align: align as any });
    };

    // Render blocks
    for (const b of blocks) {
      if (cy > pageBottom + 100) break;
      if (b.kind === 'heading') writeHeading(b.text);
      else if (b.kind === 'subheading') writeSubheading(b.text);
      else if (b.kind === 'paragraph') writeParagraph(b.text);
      else if (b.kind === 'list') writeList(b.items);
      else if (b.kind === 'quote') {
        // inline quote rendered in gold italic
        writeParagraph('"' + b.text + '"', { italic: true, color: ORANGE });
      }
    }

    // If we never reached page 2, still draw page 2 with just the quote (so output is always 2 pages)
    if (page === 1) {
      drawFooter(1);
      doc.addPage();
      page = 2;
    }
    // Ensure quote drawn even if right col never reached
    if (quoteText && !quotePlaced) {
      col = 1;
      cy = page2Top;
      drawPullQuote();
      quotePlaced = true;
    }

    drawFooter(2);

    const safe = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'quick-one';
    doc.save(`quick-one-${safe}.pdf`);
  };

  if (authChecking) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Checking access...</div>;
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-white">
      <section className="py-12 bg-primary text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Quick One Drafting</h1>
            <p className="text-blue-100">A short, two-page commentary in magazine-article format.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          <div className="space-y-6 min-w-0">
          {/* Masthead */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Issue details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="issue">Issue No.</Label>
                <Input id="issue" value={issueNumber} onChange={(e) => setIssueNumber(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="my">Month & Year</Label>
                <Input id="my" value={monthYear} onChange={(e) => setMonthYear(e.target.value)} placeholder="March 2026" />
              </div>
              <div>
                <Label htmlFor="sp">Start Page #</Label>
                <Input id="sp" value={startPage} onChange={(e) => setStartPage(e.target.value)} placeholder="12" />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Title & Hero</h2>
            <div>
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Privacy Compliance or Breach?" />
            </div>
            <div>
              <Label htmlFor="subtitle">Subtitle (rendered in CAPS)</Label>
              <Textarea id="subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} placeholder="High Court weighs in on the importance of proper consent mechanisms" />
            </div>
            <div>
              <Label htmlFor="hero">Hero Image (top of page 1) *</Label>
              <Input id="hero" type="file" accept="image/*" onChange={(e) => onImage(e, setHeroImage)} />
              {heroImage && (
                <div className="relative mt-2 w-full max-w-md border border-gray-200 rounded overflow-hidden">
                  <img src={heroImage.dataUrl} alt="Hero" className="w-full h-auto" />
                  <button type="button" onClick={() => setHeroImage(null)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1" aria-label="Remove">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Authors */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Authors (byline row)</h2>
              <Button type="button" variant="outline" size="sm" onClick={addAuthor} disabled={authors.length >= 3}>
                <Plus className="w-4 h-4 mr-2" /> Add author
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const photo = await urlToQImage(founderPortrait);
                    setAuthors([{ name: 'Omollo Victor', role: 'Trainee Advocate | omollovictor81@gmail.com', photo }]);
                  } catch {}
                }}
              >
                Set Omollo Victor
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const photo = await urlToQImage(gracePortrait);
                    setAuthors([{ name: 'Grace Gakii', role: 'Partner | LLB Hons | CPM | ATP Student, KSL', photo }]);
                  } catch {}
                }}
              >
                Set Grace Gakii
              </Button>
            </div>
            {authors.map((a, i) => (
              <div key={i} className="border border-gray-200 rounded p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Author {i + 1}</span>
                  {authors.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAuthor(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={a.name} onChange={(e) => updateAuthor(i, { name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Role | Email</Label>
                    <Input value={a.role} onChange={(e) => updateAuthor(i, { role: e.target.value })} placeholder="Associate | name@firm.com" />
                  </div>
                </div>
                <div>
                  <Label>Photo (optional)</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onImage(e, (img) => updateAuthor(i, { photo: img }))}
                  />
                  {a.photo && (
                    <img src={a.photo.dataUrl} alt={a.name} className="mt-2 w-16 h-16 object-cover rounded-full" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Body sections */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Body Sections</h2>
              <Button type="button" variant="outline" size="sm" onClick={addSection}>
                <Plus className="w-4 h-4 mr-2" /> Add subsection
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Each subsection has a heading and its own content. Separate paragraphs with a blank line.
              Use <code className="bg-white px-1 rounded">##</code> for a bold subheading inside the content,{' '}
              <code className="bg-white px-1 rounded">**bold**</code> for inline emphasis, and{' '}
              <code className="bg-white px-1 rounded">-</code> to start a bullet item.
              Keep it short — the layout is strictly two pages.
            </p>
            {sections.map((s, i) => (
              <div key={i} className="border border-gray-200 rounded p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Subsection {i + 1}</span>
                  {sections.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeSection(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div>
                  <Label>Heading</Label>
                  <Input
                    value={s.heading}
                    onChange={(e) => updateSection(i, { heading: e.target.value })}
                    placeholder="Introduction"
                  />
                </div>
                <div>
                  <Label>Content</Label>
                  <Textarea
                    value={s.content}
                    onChange={(e) => updateSection(i, { content: e.target.value })}
                    rows={8}
                    placeholder={'The Data Protection Act...\n\n- First point\n- Second point'}
                    className="font-serif"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Pull quote */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Pull Quote (optional)</h2>
            <p className="text-sm text-muted-foreground">Sits at the top of page 2's right column, italic in the accent colour.</p>
            <Textarea
              value={pullQuote}
              onChange={(e) => setPullQuote(e.target.value)}
              rows={3}
              placeholder="A standout sentence from your article..."
              className="font-serif"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleDownload} size="lg">
              <Download className="w-4 h-4 mr-2" /> Download Quick One PDF
            </Button>
          </div>
          </div>

          {/* Sticky live preview */}
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">Live Preview</h2>
              <QuickOnePreview
                title={title}
                subtitle={subtitle}
                issueNumber={issueNumber}
                monthYear={monthYear}
                startPage={startPage}
                heroImage={heroImage}
                pullQuote={pullQuote}
                authors={authors}
                blocks={parseBodyBlocks(bodyText)}
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default AdminQuickOne;
